import React, { useState, useEffect } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { setPricingData } from '../../utils/pricingHelpers';

const TABS = [
  { key: 'base_service', label: 'Base Service' },
  { key: 'urns', label: 'Urns' },
  { key: 'keepsakes', label: 'Keepsakes' },
  { key: 'jewelry', label: 'Jewelry' },
  { key: 'transportation', label: 'Transport' },
  { key: 'certificates', label: 'Certificates' },
  { key: 'viewing_room', label: 'Viewing Room' },
  { key: 'euthanasia', label: 'Euthanasia' },
  { key: 'vet_products', label: 'Vet Products' },
];

const PricingAdminModal = ({ isOpen, onClose }) => {
  const [tab, setTab] = useState('base_service');
  const [pricing, setPricing] = useState(null);
  const [vetProducts, setVetProducts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({});

  useEffect(() => {
    if (isOpen) loadPricing();
  }, [isOpen]);

  const loadPricing = async () => {
    setLoading(true);
    try {
      const [pricingRes, vetRes] = await Promise.all([
        apiClient.get('/config/pricing-all'),
        apiClient.get('/config/vet-products')
      ]);
      setPricing(pricingRes.data);
      setVetProducts(vetRes.data.products || []);
      setDirty({});
    } catch (e) {
      showToast('Failed to load pricing', 'error');
    }
    setLoading(false);
  };

  const saveCategory = async (category) => {
    setSaving(true);
    try {
      if (category === 'vet_products') {
        await apiClient.put('/config/vet-products', { products: vetProducts });
        const { data } = await apiClient.get('/config/vet-products');
        setVetProducts(data.products || []);
      } else {
        await apiClient.put(`/config/pricing/${category}`, { data: pricing[category] });
        const { data } = await apiClient.get('/config/pricing-all');
        setPricing(data);
        setPricingData(data);
      }
      setDirty(d => ({ ...d, [category]: false }));
      showToast(`${TABS.find(t => t.key === category)?.label} saved`, 'success');
    } catch (e) {
      showToast('Failed to save', 'error');
    }
    setSaving(false);
  };

  const updateField = (category, path, value) => {
    setPricing(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      let obj = updated[category];
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return updated;
    });
    setDirty(d => ({ ...d, [category]: true }));
  };

  const updateVetProduct = (productIdx, varIdx, field, value) => {
    setVetProducts(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (varIdx !== null) {
        updated[productIdx].variations[varIdx][field] = value;
      } else {
        updated[productIdx][field] = value;
      }
      return updated;
    });
    setDirty(d => ({ ...d, vet_products: true }));
  };

  const addVetVariation = (productIdx) => {
    setVetProducts(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[productIdx].variations.push({ name: '', points: 0 });
      return updated;
    });
    setDirty(d => ({ ...d, vet_products: true }));
  };

  const removeVetVariation = (productIdx, varIdx) => {
    setVetProducts(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[productIdx].variations.splice(varIdx, 1);
      return updated;
    });
    setDirty(d => ({ ...d, vet_products: true }));
  };

  const addVetProduct = () => {
    setVetProducts(prev => [...prev, { product: '', variations: [{ name: '', points: 0 }] }]);
    setDirty(d => ({ ...d, vet_products: true }));
  };

  const removeVetProduct = (productIdx) => {
    setVetProducts(prev => prev.filter((_, i) => i !== productIdx));
    setDirty(d => ({ ...d, vet_products: true }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="pricing-admin-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-emerald-600 to-teal-600 rounded-t-xl">
          <h2 className="text-xl font-bold text-white">Pricing Manager</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="pricing-modal-close">&times;</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto px-2 pt-2 bg-gray-50 flex-shrink-0 relative z-10">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-lg transition ${
                tab === t.key
                  ? 'bg-white border border-b-white -mb-px text-emerald-700 border-gray-200'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`pricing-tab-${t.key}`}
            >
              {t.label}
              {dirty[t.key] && <span className="ml-1 text-amber-500">*</span>}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
            </div>
          ) : pricing ? (
            <>
              {tab === 'base_service' && <BaseServiceTab pricing={pricing} updateField={updateField} />}
              {tab === 'urns' && <UrnsTab pricing={pricing} updateField={updateField} />}
              {tab === 'keepsakes' && <KeepsakesTab pricing={pricing} updateField={updateField} />}
              {tab === 'jewelry' && <ListTab category="jewelry" pricing={pricing} updateField={updateField} />}
              {tab === 'transportation' && <TransportTab pricing={pricing} updateField={updateField} />}
              {tab === 'certificates' && <ListTab category="certificates" pricing={pricing} updateField={updateField} />}
              {tab === 'viewing_room' && <ListTab category="viewing_room" pricing={pricing} updateField={updateField} />}
              {tab === 'euthanasia' && <EuthanasiaTab pricing={pricing} updateField={updateField} />}
              {tab === 'vet_products' && (
                <VetProductsTab
                  products={vetProducts || []}
                  updateProduct={updateVetProduct}
                  addVariation={addVetVariation}
                  removeVariation={removeVetVariation}
                  addProduct={addVetProduct}
                  removeProduct={removeVetProduct}
                />
              )}
            </>
          ) : (
            <p className="text-gray-500 text-center py-8">No pricing data available</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <p className="text-xs text-gray-400">Changes are saved per category</p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border rounded-lg"
            >
              Close
            </button>
            <button
              onClick={() => saveCategory(tab)}
              disabled={saving || !dirty[tab]}
              className={`px-5 py-2 text-sm font-medium rounded-lg text-white transition ${
                dirty[tab] && !saving
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              data-testid="pricing-save-btn"
            >
              {saving ? 'Saving...' : `Save ${TABS.find(t => t.key === tab)?.label}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Sub-components for each tab ----

const PriceInput = ({ value, onChange, label, className = '' }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    {label && <span className="text-sm text-gray-600 min-w-0">{label}</span>}
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        className="w-24 pl-6 pr-2 py-1.5 border rounded-md text-sm text-right focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
      />
    </div>
  </div>
);

const BaseServiceTab = ({ pricing, updateField }) => {
  const rules = pricing.base_service || [];
  const petTypes = [...new Set(rules.map(r => r.pet_type))];

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">Base cremation service price by pet type and weight range.</p>
      {petTypes.map(pt => (
        <div key={pt} className="border rounded-lg p-4">
          <h3 className="font-semibold text-gray-800 mb-3">{pt}</h3>
          <div className="space-y-2">
            {rules.map((rule, idx) => rule.pet_type === pt && (
              <div key={idx} className="flex items-center gap-4 bg-gray-50 rounded-lg px-4 py-2">
                <span className="text-sm text-gray-600 min-w-[120px]">
                  {rule.max_weight >= 999 ? `${rule.min_weight}+ kg` : `${rule.min_weight}-${rule.max_weight} kg`}
                </span>
                <PriceInput
                  value={rule.price}
                  onChange={v => updateField('base_service', [idx, 'price'], v)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const EuthanasiaTab = ({ pricing, updateField }) => {
  const rules = pricing.euthanasia || [];
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">Euthanasia add-on service price by weight (applies to all pet types). Added on top of the base cremation service.</p>
      <div className="border rounded-lg p-4">
        <div className="space-y-2">
          {rules.map((rule, idx) => (
            <div key={idx} className="flex items-center gap-4 bg-gray-50 rounded-lg px-4 py-2" data-testid={`euthanasia-tier-${idx}`}>
              <span className="text-sm text-gray-600 min-w-[120px]">
                {rule.max_weight >= 999 ? `${rule.min_weight}+ kg` : `${rule.min_weight}-${rule.max_weight} kg`}
              </span>
              <PriceInput
                value={rule.price}
                onChange={v => updateField('euthanasia', [idx, 'price'], v)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const UrnsTab = ({ pricing, updateField }) => {
  const urns = pricing.urns || {};

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">Urn prices organized by category.</p>
      {Object.entries(urns).map(([groupName, group]) => (
        <div key={groupName} className="border rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-4 py-2">
            <h3 className="font-semibold text-gray-800">{groupName}</h3>
          </div>
          <div className="divide-y">
            {group.options.map((opt, idx) => (
              <div key={idx} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50">
                <span className="text-sm text-gray-700">{opt.name}</span>
                <PriceInput
                  value={opt.price}
                  onChange={v => updateField('urns', [groupName, 'options', idx, 'price'], v)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const KeepsakesTab = ({ pricing, updateField }) => {
  const items = pricing.keepsakes || [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Keepsake prices. Items with pet-type-specific pricing show multiple fields.</p>
      <div className="border rounded-lg divide-y">
        {items.map((item, idx) => {
          const hasPetPricing = 'price_dog' in item || 'price_cat' in item;
          return (
            <div key={idx} className="px-4 py-3 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{item.name}</span>
                {!hasPetPricing && (
                  <PriceInput
                    value={item.price}
                    onChange={v => updateField('keepsakes', [idx, 'price'], v)}
                  />
                )}
              </div>
              {hasPetPricing && (
                <div className="flex gap-4 mt-2 pl-4">
                  <PriceInput label="Dog" value={item.price_dog || 0} onChange={v => updateField('keepsakes', [idx, 'price_dog'], v)} />
                  <PriceInput label="Cat" value={item.price_cat || 0} onChange={v => updateField('keepsakes', [idx, 'price_cat'], v)} />
                  <PriceInput label="Exotic" value={item.price_exotic || 0} onChange={v => updateField('keepsakes', [idx, 'price_exotic'], v)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ListTab = ({ category, pricing, updateField }) => {
  const items = pricing[category] || [];
  const nameKey = category === 'transportation' ? 'label' : 'name';

  return (
    <div className="space-y-4">
      <div className="border rounded-lg divide-y">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
            <span className="text-sm text-gray-700">{item[nameKey]}</span>
            <PriceInput
              value={item.price}
              onChange={v => updateField(category, [idx, 'price'], v)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const TransportTab = ({ pricing, updateField }) => {
  const items = pricing.transportation || [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">Transportation option prices. The label includes the price for display.</p>
      <div className="border rounded-lg divide-y">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
            <span className="text-sm text-gray-700">{item.label}</span>
            <PriceInput
              value={item.price}
              onChange={v => updateField('transportation', [idx, 'price'], v)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const VetProductsTab = ({ products, updateProduct, addVariation, removeVariation, addProduct, removeProduct }) => {
  const [clinicView, setClinicView] = useState('');
  const [clinicCatalogue, setClinicCatalogue] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [overrideDirty, setOverrideDirty] = useState(false);
  const [savingOverrides, setSavingOverrides] = useState(false);
  const [clinics, setClinics] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/vet-clinics`)
      .then(r => r.json())
      .then(d => setClinics(d.vet_clinics || []))
      .catch((e) => { console.error("Pricing load failed:", e); });
  }, []);

  const loadClinicView = async (name) => {
    setClinicView(name);
    setOverrideDirty(false);
    if (!name) { setClinicCatalogue(null); setOverrides([]); return; }
    try {
      const [catRes, ovRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/vet-products/for-clinic?clinic_name=${encodeURIComponent(name)}`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/vet-products/overrides?clinic_name=${encodeURIComponent(name)}`)
      ]);
      if (catRes.ok) setClinicCatalogue((await catRes.json()).products || []);
      if (ovRes.ok) setOverrides((await ovRes.json()).overrides || []);
    } catch (e) { console.error("Pricing error:", e); }
  };

  const updateOverride = (product, variation, globalPts, newPts) => {
    const val = parseInt(newPts) || 0;
    if (val === globalPts) {
      setOverrides(prev => prev.filter(o => !(o.product === product && o.variation === variation)));
    } else {
      setOverrides(prev => {
        const existing = prev.find(o => o.product === product && o.variation === variation);
        if (existing) return prev.map(o => o.product === product && o.variation === variation ? { ...o, points: val } : o);
        return [...prev, { clinic_name: clinicView, product, variation, points: val }];
      });
    }
    setOverrideDirty(true);
  };

  const saveOverrides = async () => {
    setSavingOverrides(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/vet-products/overrides`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clinic_name: clinicView, overrides })
      });
      if (res.ok) {
        showToast(`Overrides saved for ${clinicView}`, 'success');
        setOverrideDirty(false);
        await loadClinicView(clinicView);
      } else { showToast('Failed to save', 'error'); }
    } catch { showToast('Failed to save', 'error'); }
    setSavingOverrides(false);
  };

  // Find the global price for a product/variation
  const getGlobalPrice = (productName, variationName) => {
    const prod = products.find(p => p.product === productName);
    if (!prod) return 0;
    const v = prod.variations?.find(vr => vr.name === variationName);
    return v?.points || 0;
  };

  const getOverridePrice = (productName, variationName) => {
    const ov = overrides.find(o => o.product === productName && o.variation === variationName);
    return ov ? ov.points : null;
  };

  // ── Clinic-specific view ──
  if (clinicView) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => loadClinicView('')} className="text-sm text-gray-500 hover:text-gray-700">
            ← Back to global
          </button>
          <span className="text-sm font-semibold text-gray-800">{clinicView}</span>
          {overrides.length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
              {overrides.length} override{overrides.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500">Edit prices below. Changed values are highlighted. Prices revert to global when set back to the original.</p>

        {(clinicCatalogue || []).map((prod, pIdx) => (
          <div key={pIdx} className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2">
              <span className="font-semibold text-gray-800 text-sm">{prod.product}</span>
            </div>
            <div className="divide-y">
              {prod.variations.map((v, vIdx) => {
                const globalPts = getGlobalPrice(prod.product, v.name);
                const ovPrice = getOverridePrice(prod.product, v.name);
                const isOverridden = ovPrice !== null;
                const displayPts = isOverridden ? ovPrice : globalPts;
                return (
                  <div key={vIdx} className={`flex items-center justify-between px-4 py-2 ${isOverridden ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                    <span className="text-sm text-gray-700 flex-1">{v.name}</span>
                    {isOverridden && (
                      <span className="text-xs text-gray-400 line-through mr-2">{globalPts} pts</span>
                    )}
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        value={displayPts}
                        onChange={e => updateOverride(prod.product, v.name, globalPts, e.target.value)}
                        className={`w-20 text-right text-sm border rounded-md px-2 py-1.5 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${isOverridden ? 'border-amber-400 bg-amber-50' : ''}`}
                      />
                      <span className="text-xs text-gray-400">pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {overrideDirty && (
          <div className="flex justify-end">
            <button
              onClick={saveOverrides}
              disabled={savingOverrides}
              className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 text-white text-sm font-medium px-5 py-2 rounded-lg"
            >
              {savingOverrides ? 'Saving...' : `Save ${clinicView} Overrides`}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Global view (default) ──
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-500">Global prices for all clinics. Select a clinic to set custom prices.</p>
        <div className="flex gap-2">
          <select
            value=""
            onChange={e => { if (e.target.value) loadClinicView(e.target.value); }}
            className="text-sm border rounded-lg px-3 py-1.5 text-gray-600 focus:ring-1 focus:ring-emerald-500"
            data-testid="clinic-override-select"
          >
            <option value="">Clinic overrides...</option>
            {clinics.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button
            onClick={addProduct}
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            data-testid="add-vet-product-btn"
          >
            + Add Product
          </button>
        </div>
      </div>
      {products.map((prod, pIdx) => (
        <div key={pIdx} className="border rounded-lg overflow-hidden">
          <div className="bg-gray-100 px-4 py-2 flex items-center gap-3">
            <input
              type="text"
              value={prod.product}
              onChange={e => updateProduct(pIdx, null, 'product', e.target.value)}
              placeholder="Product name"
              className="flex-1 bg-transparent font-semibold text-gray-800 text-sm border-none focus:outline-none focus:ring-0 p-0"
            />
            <button
              onClick={() => removeProduct(pIdx)}
              className="text-red-400 hover:text-red-600 text-xs"
              title="Remove product"
            >
              Remove
            </button>
          </div>
          <div className="divide-y">
            {prod.variations.map((v, vIdx) => (
              <div key={vIdx} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50">
                <input
                  type="text"
                  value={v.name}
                  onChange={e => updateProduct(pIdx, vIdx, 'name', e.target.value)}
                  placeholder="Variation name"
                  className="flex-1 text-sm border rounded-md px-2 py-1.5 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    value={v.points}
                    onChange={e => updateProduct(pIdx, vIdx, 'points', parseInt(e.target.value) || 0)}
                    className="w-20 text-right text-sm border rounded-md px-2 py-1.5 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <span className="text-xs text-gray-400">pts</span>
                </div>
                <button
                  onClick={() => removeVariation(pIdx, vIdx)}
                  className="text-red-300 hover:text-red-500 text-lg leading-none"
                  title="Remove variation"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
          <div className="px-4 py-2 border-t bg-gray-50">
            <button
              onClick={() => addVariation(pIdx)}
              className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
            >
              + Add variation
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PricingAdminModal;
